import React, { useRef } from "react";
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";

const pillars = [
  {
    title: "Build",
    text: "Native-feeling apps and games, designed and engineered in-house with a small, fast team.",
  },
  {
    title: "Publish",
    text: "Store listings, compliance and release pipelines across the App Store, Google Play and web.",
  },
  {
    title: "Grow",
    text: "Analytics, live updates and ad monetization that keep products alive long after launch.",
  },
  {
    title: "Partner",
    text: "Independent developer with something great? We co-publish and bring it to a wider audience.",
  },
];

const TiltCard = ({ index, title, text }) => {
  const reduceMotion = useReducedMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-14, 14]), { stiffness: 150, damping: 15 });
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [12, -12]), { stiffness: 150, damping: 15 });
  const glowX = useTransform(x, [-0.5, 0.5], ["0%", "100%"]);
  const glowY = useTransform(y, [-0.5, 0.5], ["0%", "100%"]);
  const glow = useTransform(
    [glowX, glowY],
    ([gx, gy]) => `radial-gradient(420px circle at ${gx} ${gy}, rgba(255,138,31,0.22), transparent 60%)`
  );

  const onMove = (e) => {
    if (reduceMotion) return;
    const r = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - r.left) / r.width - 0.5);
    y.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.article
      className="pillar"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX, rotateY }}
      initial={{ opacity: 0, y: 60, rotateX: 30 }}
      whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
    >
      <motion.div className="pillar-glow" style={{ background: glow }} />
      <span className="pillar-num">{String(index + 1).padStart(2, "0")}</span>
      <h3 className="pillar-title">{title}</h3>
      <p className="pillar-text">{text}</p>
      <span className="pillar-line" />
    </motion.article>
  );
};

const Studio = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "start start"] });
  const headRotate = useTransform(scrollYProgress, [0, 1], [45, 0]);
  const headY = useTransform(scrollYProgress, [0, 1], [80, 0]);

  return (
    <section id="studio" className="studio" ref={ref}>
      <motion.div className="section-head" style={{ rotateX: headRotate, y: headY }}>
        <span className="eyebrow">
          <span className="dot" /> Studio
        </span>
        <h2 className="section-title">
          Simple products.
          <br />
          <span className="accent">Serious engineering.</span>
        </h2>
      </motion.div>
      <div className="pillars">
        {pillars.map((p, i) => (
          <TiltCard key={p.title} index={i} {...p} />
        ))}
      </div>
    </section>
  );
};

export default Studio;
